/*
 * Lecteur d'extraits audio de Send Return Studio.
 *
 * Ce script ne dessine rien : les formes d'onde sont des SVG deja presents
 * dans la page (snippet onde-svg.liquid). Il ne sert qu'a lire le son, a
 * faire avancer la progression et a tenir les etats (bouton, aria).
 *
 * Fonctionnement :
 *   - chaque extrait est un element [data-sr-extrait] portant data-fichier
 *     (adresse du mp3) et, si connue, data-duree (secondes) ;
 *   - rien n'est telecharge avant le premier clic : la balise <audio> est
 *     creee a ce moment-la, avec preload="none" ;
 *   - une seule lecture a la fois sur toute la page ;
 *   - la progression est ecrite dans la variable CSS --sr-progression (0 a 1)
 *     sur l'element [data-sr-extrait], ce qui deplace le masque du SVG ;
 *   - quand un extrait arrive au bout, la lecture enchaine d'elle-meme sur
 *     l'extrait suivant du meme conteneur [data-sr-extraits], s'il existe et
 *     s'il est visible (offsetParent non nul). Un extrait replie par le
 *     bouton « Show all » sur mobile, ou masque d'une autre facon, arrete
 *     l'enchainement. Pas d'enchainement entre deux conteneurs differents :
 *     une carte produit ne lance jamais la carte voisine. L'enchainement
 *     ne concerne que la fin naturelle (sr:pause avec fin: true), jamais
 *     une pause demandee par l'utilisateur.
 *
 * Controleur global : window.SendReturnAudio
 *   lire(element)      lance la lecture de cet extrait, coupe les autres
 *   pause(element)     met en pause cet extrait (sans argument : celui en cours)
 *   basculer(element)  lecture ou pause selon l'etat
 *   chercher(element, ratio)  se place a une position (0 a 1)
 *   preparer(racine)   branche les extraits trouves sous racine
 *   enCours()          l'element en lecture, ou null
 *
 * Evenements envoyes sur document, pour d'autres composants (cartes
 * produit, par exemple) :
 *   sr:lecture  detail : { element, fichier }   une lecture demarre
 *   sr:pause    detail : { element, fichier, fin }   une lecture s'arrete
 *               (fin vaut true quand l'extrait est arrive au bout)
 *
 * Le script est charge en differe, une seule fois. S'il est charge deux
 * fois, la seconde execution se contente de rebrancher les extraits.
 */
(function () {
  'use strict';

  if (window.SendReturnAudio) {
    window.SendReturnAudio.preparer(document);
    return;
  }

  var enCours = null; // l'element [data-sr-extrait] en lecture, ou null

  var TEXTE_LIRE = 'Play ';
  var TEXTE_PAUSE = 'Pause ';
  var TEXTE_ERREUR = 'Preview unavailable';
  var PAS_CLAVIER = 0.05; // 5 % par touche fleche

  /* ---------- petits outils ---------- */

  function formaterDuree(secondes) {
    if (!isFinite(secondes) || secondes < 0) return '--:--';
    var total = Math.floor(secondes);
    var minutes = Math.floor(total / 60);
    var reste = total % 60;
    return minutes + ':' + (reste < 10 ? '0' : '') + reste;
  }

  function borner(valeur) {
    if (!isFinite(valeur)) return 0;
    return Math.min(1, Math.max(0, valeur));
  }

  function envoyer(nom, element, detail) {
    var donnees = detail || {};
    donnees.element = element;
    donnees.fichier = element.dataset.fichier;
    document.dispatchEvent(new CustomEvent(nom, { detail: donnees }));
  }

  function bouton(element) {
    return element.querySelector('[data-sr-bouton]');
  }

  function onde(element) {
    return element.querySelector('[data-sr-onde]');
  }

  function nom(element) {
    return element.dataset.nom || 'this demo';
  }

  /* Duree connue : celle du metachamp d'abord, sinon celle du fichier. */
  function duree(element) {
    var annoncee = parseFloat(element.dataset.duree);
    if (isFinite(annoncee) && annoncee > 0) return annoncee;
    var audio = element.srAudio;
    if (audio && isFinite(audio.duration) && audio.duration > 0) return audio.duration;
    return NaN;
  }

  /* ---------- etats visuels ---------- */

  function ecrireProgression(element, ratio) {
    ratio = borner(ratio);
    element.style.setProperty('--sr-progression', ratio.toFixed(4));
    var curseur = onde(element);
    if (!curseur) return;
    var pourcent = Math.round(ratio * 100);
    curseur.setAttribute('aria-valuenow', String(pourcent));
    var totale = duree(element);
    if (isFinite(totale)) {
      curseur.setAttribute(
        'aria-valuetext',
        formaterDuree(ratio * totale) + ' of ' + formaterDuree(totale)
      );
    }
  }

  function marquerLecture(element) {
    if (enCours && enCours !== element) {
      pause(enCours);
    }
    enCours = element;
    element.classList.add('sr-extrait--joue');
    var declencheur = bouton(element);
    if (declencheur) {
      declencheur.setAttribute('aria-label', TEXTE_PAUSE + nom(element));
      declencheur.setAttribute('aria-pressed', 'true');
    }
    envoyer('sr:lecture', element);
  }

  function marquerArret(element, fin) {
    element.classList.remove('sr-extrait--joue');
    var declencheur = bouton(element);
    if (declencheur) {
      declencheur.setAttribute('aria-label', TEXTE_LIRE + nom(element));
      declencheur.setAttribute('aria-pressed', 'false');
    }
    if (enCours === element) enCours = null;
    envoyer('sr:pause', element, { fin: fin === true });
    if (fin === true) enchainer(element);
  }

  /* ---------- enchainement automatique ---------- */

  /* L'extrait qui suit celui-ci dans le meme conteneur [data-sr-extraits],
     ou null s'il n'y en a pas ou s'il est masque (offsetParent nul, ce qui
     est le cas d'un extrait replie par « Show all » sur mobile). */
  function suivantVisible(element) {
    if (typeof element.closest !== 'function') return null;
    var conteneur = element.closest('[data-sr-extraits]');
    if (!conteneur) return null;
    var extraits = conteneur.querySelectorAll('[data-sr-extrait]');
    var index = Array.prototype.indexOf.call(extraits, element);
    if (index < 0 || index + 1 >= extraits.length) return null;
    var suivant = extraits[index + 1];
    if (!suivant.offsetParent) return null;
    return suivant;
  }

  /* Appele a la fin naturelle d'un extrait, apres l'envoi de sr:pause.
     Un extrait suivant en erreur n'est pas lance : lire() l'ignore. */
  function enchainer(element) {
    var suivant = suivantVisible(element);
    if (suivant) lire(suivant);
  }

  function marquerErreur(element) {
    element.classList.add('sr-extrait--erreur');
    element.classList.remove('sr-extrait--joue');
    var cible = element.querySelector('[data-sr-duree]');
    if (cible) cible.textContent = TEXTE_ERREUR;
    var declencheur = bouton(element);
    if (declencheur) {
      declencheur.setAttribute('aria-disabled', 'true');
      declencheur.setAttribute('aria-label', TEXTE_ERREUR);
    }
    if (enCours === element) enCours = null;
  }

  function ecrireDuree(element) {
    var cible = element.querySelector('[data-sr-duree]');
    if (!cible || element.classList.contains('sr-extrait--erreur')) return;
    var totale = duree(element);
    if (isFinite(totale)) cible.textContent = formaterDuree(totale);
  }

  /* ---------- la balise audio, creee au premier clic ---------- */

  function obtenirAudio(element) {
    if (element.srAudio) return element.srAudio;
    var fichier = element.dataset.fichier;
    if (!fichier) return null;

    var audio = document.createElement('audio');
    audio.preload = 'none';
    audio.src = fichier;

    audio.addEventListener('play', function () {
      marquerLecture(element);
    });

    audio.addEventListener('pause', function () {
      // "ended" arrive juste apres "pause" : on laisse ended remettre a zero.
      marquerArret(element, audio.ended);
    });

    audio.addEventListener('timeupdate', function () {
      var totale = duree(element);
      if (!isFinite(totale) || totale <= 0) return;
      ecrireProgression(element, audio.currentTime / totale);
    });

    audio.addEventListener('ended', function () {
      ecrireProgression(element, 0);
      if (enCours === element) enCours = null;
    });

    audio.addEventListener('loadedmetadata', function () {
      ecrireDuree(element);
      // Une position demandee avant le chargement s'applique maintenant.
      if (typeof element.srPositionVoulue === 'number') {
        var ratio = element.srPositionVoulue;
        element.srPositionVoulue = null;
        appliquerPosition(element, ratio);
      }
    });

    audio.addEventListener('error', function () {
      marquerErreur(element);
    });

    element.srAudio = audio;
    return audio;
  }

  function appliquerPosition(element, ratio) {
    var audio = element.srAudio;
    var totale = duree(element);
    if (!audio || !isFinite(totale) || totale <= 0) return;
    try {
      audio.currentTime = borner(ratio) * totale;
    } catch (erreur) {
      // Certains navigateurs refusent avant les metadonnees : on attend.
      element.srPositionVoulue = borner(ratio);
    }
  }

  /* ---------- commandes ---------- */

  function lire(element) {
    if (!element || element.classList.contains('sr-extrait--erreur')) return;
    var audio = obtenirAudio(element);
    if (!audio) return;

    if (enCours && enCours !== element) pause(enCours);

    var promesse = audio.play();
    if (promesse && typeof promesse.catch === 'function') {
      promesse.catch(function (erreur) {
        // AbortError : un pause() ou un changement de source a interrompu la
        // demande, ce n'est pas une panne. Le reste (iOS sans geste, format
        // refuse, reseau) remet simplement l'extrait a l'arret ; l'evenement
        // error de la balise audio prend le relais si le fichier est en cause.
        if (erreur && erreur.name === 'AbortError') return;
        marquerArret(element, false);
      });
    }
  }

  function pause(element) {
    var cible = element || enCours;
    if (!cible || !cible.srAudio) return;
    if (!cible.srAudio.paused) cible.srAudio.pause();
  }

  function basculer(element) {
    if (!element) return;
    var audio = element.srAudio;
    if (audio && !audio.paused) {
      pause(element);
    } else {
      lire(element);
    }
  }

  /* Se placer a une position (0 a 1). Lance la lecture si elle est arretee. */
  function chercher(element, ratio) {
    if (!element || element.classList.contains('sr-extrait--erreur')) return;
    ratio = borner(ratio);
    ecrireProgression(element, ratio);

    var audio = obtenirAudio(element);
    if (!audio) return;

    if (audio.readyState >= 1) {
      appliquerPosition(element, ratio);
    } else {
      element.srPositionVoulue = ratio;
    }
    if (audio.paused) lire(element);
  }

  function positionActuelle(element) {
    var audio = element.srAudio;
    var totale = duree(element);
    if (typeof element.srPositionVoulue === 'number') return element.srPositionVoulue;
    if (!audio || !isFinite(totale) || totale <= 0) return 0;
    return borner(audio.currentTime / totale);
  }

  /* ---------- branchement d'un extrait ---------- */

  function brancher(element) {
    if (element.srBranche) return;
    element.srBranche = true;

    ecrireProgression(element, 0);
    ecrireDuree(element);

    var declencheur = bouton(element);
    if (declencheur) {
      declencheur.addEventListener('click', function (evenement) {
        evenement.preventDefault();
        basculer(element);
      });
    }

    var curseur = onde(element);
    if (!curseur) return;

    curseur.addEventListener('click', function (evenement) {
      var cadre = curseur.getBoundingClientRect();
      if (cadre.width <= 0) return;
      chercher(element, (evenement.clientX - cadre.left) / cadre.width);
    });

    curseur.addEventListener('keydown', function (evenement) {
      var touche = evenement.key;
      var position = positionActuelle(element);
      if (touche === 'ArrowLeft' || touche === 'ArrowDown') {
        evenement.preventDefault();
        chercher(element, position - PAS_CLAVIER);
      } else if (touche === 'ArrowRight' || touche === 'ArrowUp') {
        evenement.preventDefault();
        chercher(element, position + PAS_CLAVIER);
      } else if (touche === 'Home') {
        evenement.preventDefault();
        chercher(element, 0);
      } else if (touche === 'End') {
        evenement.preventDefault();
        chercher(element, 1);
      } else if (touche === ' ' || touche === 'Enter') {
        evenement.preventDefault();
        basculer(element);
      }
    });
  }

  function preparer(racine) {
    var elements = (racine || document).querySelectorAll('[data-sr-extrait]');
    Array.prototype.forEach.call(elements, brancher);
  }

  /* ---------- demarrage ---------- */

  window.SendReturnAudio = {
    lire: lire,
    pause: pause,
    basculer: basculer,
    chercher: chercher,
    preparer: preparer,
    enCours: function () {
      return enCours;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      preparer(document);
    });
  } else {
    preparer(document);
  }

  // L'editeur de theme reconstruit une section sans recharger la page.
  document.addEventListener('shopify:section:load', function (evenement) {
    preparer(evenement.target);
  });

  document.addEventListener('shopify:section:unload', function (evenement) {
    if (enCours && evenement.target && evenement.target.contains(enCours)) {
      pause(enCours);
    }
  });
})();
